import React, { useState, useRef, useEffect, useContext, useCallback, useMemo } from 'react';
import styles from './CaliChatBot.module.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '../../hooks/useAuthUser';
import { UserContext } from '../../contexts/UserContext';
import { MdSend, MdSmartToy, MdPerson, MdContentCopy, MdAdd, MdRestaurant } from 'react-icons/md';
import Modal from '../../components/Modal/Modal';
import { normalizeMealPlan, getMealEntriesForDay } from '../../utils/mealPlan';
import { saveMealPlan } from '../../utils/generateMeals';
import { toast } from 'react-toastify';

const CODE_BLOCK_REGEX = /```(?:suggestions|routine|mealplan|json|javascript|js|python|py|html|css|code)?\s*[\s\S]*?```/g;

const parseRoutineBlock = (content) => {
	if (!content || typeof content !== 'string') return null;
	try {
		const routineMatch = content.match(/```routine\n([\s\S]*?)```/);
		if (routineMatch) return JSON.parse(routineMatch[1]);
	} catch { /* malformed JSON */ }
	return null;
};

const parseMealPlanBlock = (content) => {
	if (!content || typeof content !== 'string') return null;
	try {
		const mealMatch = content.match(/```mealplan\n([\s\S]*?)```/);
		if (mealMatch) {
			const raw = JSON.parse(mealMatch[1]);
			return normalizeMealPlan(raw);
		}
	} catch { /* malformed JSON */ }
	return null;
};

const stripCodeBlocks = (text) => {
	if (!text || typeof text !== 'string') return '';
	try {
		return text
			.replace(CODE_BLOCK_REGEX, '')
			.replace(/`[^`]+`/g, (match) => match.replace(/`/g, ''))
			.trim();
	} catch { return text; }
};

const copyToClipboard = (text) => {
	try {
		const cleaned = (text || '').replace(CODE_BLOCK_REGEX, '').trim();
		navigator.clipboard.writeText(cleaned);
	} catch {
		toast.error('Could not copy to clipboard');
	}
};

const formatMessage = (content) => {
	const cleaned = stripCodeBlocks(content);
	if (!cleaned) return null;
	return cleaned.split('\n').map((line, i) => {
		if (line.startsWith('**') && line.endsWith('**')) {
			return <p key={i} className={styles.BoldLine}>{line.replace(/\*\*/g, '')}</p>;
		}
		const boldInline = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
		if (line.startsWith('- ')) {
			return <p key={i} className={styles.ListItem} dangerouslySetInnerHTML={{ __html: boldInline.substring(2) }} />;
		}
		if (line.match(/^\d+\./)) {
			return <p key={i} className={styles.ListItem} dangerouslySetInnerHTML={{ __html: boldInline }} />;
		}
		if (line.trim() === '') return <br key={i} />;
		if (boldInline !== line) {
			return <p key={i} dangerouslySetInnerHTML={{ __html: boldInline }} />;
		}
		return <p key={i}>{line}</p>;
	});
};

const QUICK_PROMPTS = [
	"Create a push day routine",
	"What should I eat after a workout?",
	"Help me build a 4-day split",
	"How do I improve my squat form?"
];

const ChatMessage = React.memo(({ msg, onAddRoutine, onAddMealPlan }) => {
	const routine = useMemo(
		() => msg.role === 'assistant' ? parseRoutineBlock(msg.content) : null,
		[msg.role, msg.content]
	);
	const mealPlan = useMemo(
		() => msg.role === 'assistant' ? parseMealPlanBlock(msg.content) : null,
		[msg.role, msg.content]
	);
	const displayContent = useMemo(() => {
		try {
			return (msg.content || '').replace(CODE_BLOCK_REGEX, '').trim();
		} catch { return msg.content || ''; }
	}, [msg.content]);

	return (
		<div className={`${styles.MessageRow} ${msg.role === 'user' ? styles.UserRow : styles.AssistantRow}`}>
			{msg.role === 'assistant' && (
				<div className={styles.AvatarSmall}>
					<MdSmartToy />
				</div>
			)}
			<div className={`${styles.MessageBubble} ${msg.role === 'user' ? styles.UserBubble : styles.AssistantBubble}`}>
				<div className={styles.MessageText}>
					{formatMessage(displayContent)}
				</div>
				{routine && (
					<div className={styles.RoutinePreview}>
						<div className={styles.RoutinePreviewHeader}>
							<span className={styles.RoutinePreviewName}>{routine.routineName}</span>
							<span className={styles.RoutinePreviewTime}>{routine.estimatedTimeMinutes} min</span>
						</div>
						<div className={styles.RoutinePreviewWorkouts}>
							{routine.workouts?.map((w, j) => (
								<span key={j} className={styles.WorkoutPill}>
									{w.workoutType} · {w.workoutSets}×{w.workoutReps}
								</span>
							))}
						</div>
						<button className={styles.AddRoutineBtn} onClick={() => onAddRoutine(routine)}>
							<MdAdd /> Add to My Routines
						</button>
					</div>
				)}
				{mealPlan && (
					<div className={styles.MealPlanPreview}>
						<div className={styles.MealPlanPreviewHeader}>
							<div className={styles.MealPlanTitleRow}>
								<MdRestaurant className={styles.MealPlanIcon} />
								<span className={styles.MealPlanName}>{mealPlan.planName}</span>
							</div>
							<span className={styles.MealPlanBadge}>7-day plan</span>
						</div>
						{mealPlan.reason && (
							<p className={styles.MealPlanReason}>{mealPlan.reason}</p>
						)}
						<div className={styles.MealPlanDays}>
							{mealPlan.days?.slice(0, 3).map((day, di) => {
								const entries = getMealEntriesForDay(day);
								const dayCals = entries.reduce((sum, e) => sum + (e.calories || 0), 0);
								return (
									<div key={di} className={styles.MealDayCard}>
										<div className={styles.MealDayHeader}>
											<span className={styles.MealDayLabel}>Day {di + 1}</span>
											{dayCals > 0 && <span className={styles.MealDayCals}>{dayCals} cal</span>}
										</div>
										{entries.map(({ mealType, name }) => (
											<div key={mealType} className={styles.MealDayRow}>
												<span className={styles.MealDayType}>{mealType}</span>
												<span className={styles.MealDayDish}>{name}</span>
											</div>
										))}
									</div>
								);
							})}
						</div>
						{mealPlan.days?.length > 3 && (
							<p className={styles.MealPlanMore}>+ {mealPlan.days.length - 3} more days included</p>
						)}
						<button className={styles.AddMealPlanBtn} onClick={() => onAddMealPlan(mealPlan)}>
							<MdAdd /> Add to My Meal Plans
						</button>
					</div>
				)}
				{msg.role === 'assistant' && displayContent && (
					<button className={styles.CopyBtn} onClick={() => copyToClipboard(displayContent)} title="Copy">
						<MdContentCopy />
					</button>
				)}
			</div>
			{msg.role === 'user' && (
				<div className={styles.AvatarSmallUser}>
					<MdPerson />
				</div>
			)}
		</div>
	);
});

const CaliChatBot = () => {
	const navigate = useNavigate();
	const { user } = useAuthUser();
	const { dbUser } = useContext(UserContext);
	const API_URL = process.env.REACT_APP_API_URL;

	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const messagesEndRef = useRef(null);
	const inputRef = useRef(null);

	const scrollToBottom = useCallback(() => {
		requestAnimationFrame(() => {
			messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
		});
	}, []);

	useEffect(() => {
		scrollToBottom();
	}, [messages, scrollToBottom]);

	useEffect(() => {
		if (messages.length === 0) {
			setMessages([{
				role: 'assistant',
				content: `Hey${dbUser?.username ? ` ${dbUser.username}` : ''}! I'm Cali, your fitness AI. I can help you build routines, plan meals, answer workout questions, or just chat about your fitness goals. What's on your mind?`
			}]);
		}
	}, [dbUser?.username]); // eslint-disable-line react-hooks/exhaustive-deps

	const buildWorkoutContext = useCallback(() => {
		if (!dbUser) return {};

		const routines = dbUser?.stryds?.routines || [];
		const activeRoutines = routines
			.filter(r => r.status !== 'draft')
			.slice(0, 3)
			.map(r => ({
				name: r.routineName,
				workouts: (r.workouts || []).map(w => w.workoutType).join(', '),
				estimatedMinutes: r.estimatedTimeMinutes,
			}));

		const joinedRoutines = (dbUser?.stryds?.joinedRoutines || [])
			.slice(0, 5)
			.map(r => ({
				name: r.routineName,
				creator: r.routineCreator,
				estimatedMinutes: r.estimatedTimeMinutes,
			}));

		const recentWorkouts = (dbUser?.workoutLog || [])
			.slice(-5)
			.map(w => ({
				routineName: w.routineName,
				completedAt: w.completedAt,
				durationMinutes: w.durationMinutes,
			}));

		return {
			activeRoutines,
			joinedRoutines,
			recentWorkouts,
			trainingDaysPerWeek: dbUser?.interests?.trainingDaysPerWeek || null,
			fitnessGoal: dbUser?.interests?.goal || null,
			experienceLevel: dbUser?.interests?.level || null,
		};
	}, [dbUser]);

	const sendMessage = async () => {
		if (!input.trim() || isLoading) return;

		const userMessage = { role: 'user', content: input.trim() };
		const updatedMessages = [...messages, userMessage];
		setMessages(updatedMessages);
		setInput('');
		setIsLoading(true);

		try {
			const apiMessages = updatedMessages.filter(m => m.role !== 'system');
			const workoutContext = buildWorkoutContext();

			const response = await axios.post(`${API_URL}/cali/chat`, {
				messages: apiMessages,
				userContext: {
					userID: user?.id,
					username: dbUser?.username || user?.firstName,
					totalXP: dbUser?.totalXP || 0,
					streak: dbUser?.streak,
					interests: dbUser?.interests,
					routineCount: dbUser?.stryds?.routines?.length || 0,
					userPlan: dbUser?.userPlan || 'free',
					...workoutContext,
				}
			});

			setMessages(prev => [...prev, {
				role: 'assistant',
				content: response.data.message
			}]);
		} catch (error) {
			if (error?.response?.status === 403) {
				toast.error('Cali is only available on a paid plan.');
				navigate('/upgrade');
				return;
			}
			setMessages(prev => [...prev, {
				role: 'assistant',
				content: "Oops, I hit a wall there. Try again in a sec?"
			}]);
		} finally {
			setIsLoading(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	};

	const addRoutineToStryds = useCallback(async (routine) => {
		if (!user?.id) return;
		try {
			const today = new Date().toISOString().split('T')[0];
			await axios.post(`${API_URL}/stryds/${user.id}`, {
				...routine,
				routineCreationType: 'generated',
				createdDate: today,
				participants: [{
					userID: user.id,
					username: dbUser?.username || user.firstName,
					profileImageLink: user.profileImageUrl,
					userRoutineXP: 0
				}]
			});
			setMessages(prev => [...prev, {
				role: 'assistant',
				content: `Added "${routine.routineName}" to your routines! Go crush it.`
			}]);
		} catch {
			toast.error("Couldn't add the routine right now. Try again?");
		}
	}, [user, dbUser, API_URL]);

	const addMealPlanToPlans = useCallback(async (mealPlan) => {
		if (!user?.id) return;
		try {
			await saveMealPlan(user.id, mealPlan);
			setMessages(prev => [...prev, {
				role: 'assistant',
				content: `Saved "${mealPlan.planName}" to your meal plans! Eat well.`
			}]);
		} catch {
			toast.error("Couldn't save the meal plan right now. Try again?");
		}
	}, [user]);

	const clearChat = useCallback(() => {
		setMessages([{
			role: 'assistant',
			content: `Fresh start! What are we working on${dbUser?.username ? `, ${dbUser.username}` : ''}?`
		}]);
	}, [dbUser?.username]);

	const closeModal = useCallback(() => {
		const canGoBack = typeof window !== 'undefined' && window.history.length > 1;
		if (canGoBack) {
			navigate(-1);
			return;
		}
		navigate('/');
	}, [navigate]);

	return (
		<div className={styles.ChatModalRoot}>
			<Modal
				onClose={closeModal}
				title='Cali'
				subtitle={isLoading ? 'Thinking...' : 'AI Fitness Assistant'}
				maxWidth={820}
				overlayClassName={styles.CaliOverlay}
				contentClassName={styles.CaliModalContent}
				bodyClassName={styles.ChatModalBody}
				headerRight={(
					<button
						type='button'
						className={styles.NewChatBtn}
						onClick={clearChat}
						title='New chat'
						aria-label='Start a new Cali chat'
					>
						<MdAdd />
					</button>
				)}
			>
				<div className={styles.ChatPage}>
					<div className={styles.MessagesContainer}>
						{messages.length <= 1 && (
							<div className={styles.QuickPrompts}>
								{QUICK_PROMPTS.map((prompt, i) => (
									<button
										key={i}
										className={styles.QuickPromptBtn}
										onClick={() => { setInput(prompt); inputRef.current?.focus(); }}
									>
										{prompt}
									</button>
								))}
							</div>
						)}

						{messages.map((msg, i) => (
							<ChatMessage
								key={i}
								msg={msg}
								onAddRoutine={addRoutineToStryds}
								onAddMealPlan={addMealPlanToPlans}
							/>
						))}

						{isLoading && (
							<div className={`${styles.MessageRow} ${styles.AssistantRow}`}>
								<div className={styles.AvatarSmall}><MdSmartToy /></div>
								<div className={`${styles.MessageBubble} ${styles.AssistantBubble}`}>
									<div className={styles.TypingIndicator}>
										<span></span><span></span><span></span>
									</div>
								</div>
							</div>
						)}
						<div ref={messagesEndRef} />
					</div>

					<div className={styles.InputArea}>
						<div className={styles.InputWrapper}>
							<textarea
								ref={inputRef}
								className={styles.ChatInput}
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder="Ask Cali anything about fitness..."
								rows={1}
								disabled={isLoading}
							/>
							<button
								className={`${styles.SendBtn} ${input.trim() ? styles.SendBtnActive : ''}`}
								onClick={sendMessage}
								disabled={!input.trim() || isLoading}
							>
								<MdSend />
							</button>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default CaliChatBot;
